import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import Loader from "@/components/loader";
import SiteSetup from "@/components/site-setup";
import type { Site } from "@/components/site-setup-types";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Heading, Text } from "@/components/ui/typography";
import { getErrorMessage } from "@/lib/form-errors";
import { useUser } from "@/lib/user-context";
import { trpc } from "@/utils/trpc";

const profileBasicsSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required"),
  company: z.string().trim().min(1, "Company is required"),
  jobTitle: z.string().trim().min(1, "Job title is required"),
});

type OnboardingProfileBasics = z.infer<typeof profileBasicsSchema>;
type OnboardingProfileBasicsField = keyof OnboardingProfileBasics;
type OnboardingProfileErrors = Partial<
  Record<OnboardingProfileBasicsField | "general", string>
>;

type OnboardingProfile = {
  fullName?: string | null;
  company?: string | null;
  jobTitle?: string | null;
};
type OnboardingInviteRole = "site_manager" | "worker";

type QueuedOnboardingInvite = {
  email: string;
  role: OnboardingInviteRole;
};

const inviteEmailSchema = z
  .string()
  .trim()
  .email("Enter a valid email address");
const totalSteps = 3;
const onboardingSiteSetupFormId = "onboarding-site-setup-form";
const profileBasicsFields: ReadonlyArray<{
  key: OnboardingProfileBasicsField;
  label: string;
  placeholder: string;
}> = [
  {
    key: "fullName",
    label: "Full name",
    placeholder: "Enter your full name",
  },
  {
    key: "company",
    label: "Company",
    placeholder: "Enter your company name",
  },
  {
    key: "jobTitle",
    label: "Job title",
    placeholder: "Site manager, project lead, supervisor",
  },
];

function buildInitialProfileBasics(
  profile: OnboardingProfile | null | undefined
): OnboardingProfileBasics {
  return {
    fullName: profile?.fullName ?? "",
    company: profile?.company ?? "",
    jobTitle: profile?.jobTitle ?? "",
  };
}

function parseProfileBasics(
  profile: OnboardingProfile | null | undefined
): OnboardingProfileBasics | null {
  const result = profileBasicsSchema.safeParse(profile);
  return result.success ? result.data : null;
}

function buildProfileBasicsErrors(
  error: z.ZodError<OnboardingProfileBasics>
): OnboardingProfileErrors {
  const fieldErrors = error.flatten().fieldErrors;

  return {
    fullName: fieldErrors.fullName?.[0],
    company: fieldErrors.company?.[0],
    jobTitle: fieldErrors.jobTitle?.[0],
  };
}

function OnboardingContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, isSessionLoading } = useUser();
  const [stepIndex, setStepIndex] = useState(0);
  const [createdSite, setCreatedSite] = useState<Site | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OnboardingInviteRole>("worker");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [queuedInvites, setQueuedInvites] = useState<QueuedOnboardingInvite[]>(
    []
  );
  const [hasHydratedProfileBasics, setHasHydratedProfileBasics] =
    useState(false);
  const userProfileQueryOptions = trpc.getUserProfile.queryOptions();
  const {
    data: userProfileData,
    isPending: userProfilePending,
    isError: userProfileIsError,
    error: userProfileErrorValue,
  } = useQuery({
    ...userProfileQueryOptions,
    enabled: Boolean(session?.user?.id),
  });
  const [profileBasics, setProfileBasics] = useState<OnboardingProfileBasics>({
    fullName: "",
    company: "",
    jobTitle: "",
  });
  const [profileErrors, setProfileErrors] = useState<OnboardingProfileErrors>(
    {}
  );

  const setCachedUserProfile = (
    updatedProfile: NonNullable<typeof userProfileData>
  ) => {
    queryClient.setQueryData(userProfileQueryOptions.queryKey, updatedProfile);
  };

  useEffect(() => {
    if (!userProfileData || hasHydratedProfileBasics) {
      return;
    }

    setProfileBasics(buildInitialProfileBasics(userProfileData));
    setHasHydratedProfileBasics(true);

    if (parseProfileBasics(userProfileData)) {
      setStepIndex((current) => Math.max(current, 1));
    }
  }, [hasHydratedProfileBasics, userProfileData]);

  const updateProfileMutation = useMutation({
    ...trpc.updateUserProfile.mutationOptions(),
    onSuccess: (updatedProfile) => {
      setCachedUserProfile(updatedProfile);
      setProfileErrors({});
      setStepIndex(1);
    },
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to save profile details");
      setProfileErrors({ general: message });
      toast.error(message);
    },
  });

  const updateOnboardingStatusMutation = useMutation({
    ...trpc.updateOnboardingStatus.mutationOptions(),
    onSuccess: (updatedProfile) => {
      setCachedUserProfile(updatedProfile);
    },
  });
  const sendInviteMutation = useMutation({
    ...trpc.sendOnboardingInvite.mutationOptions(),
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to send invite");
      setInviteError(message);
      toast.error(message);
    },
  });

  const isBusy =
    updateProfileMutation.isPending ||
    updateOnboardingStatusMutation.isPending ||
    sendInviteMutation.isPending;
  const persistStatusAndExit = async (
    onboardingStatus: "skipped" | "completed"
  ) => {
    await updateOnboardingStatusMutation.mutateAsync({ onboardingStatus });
    navigate("/dashboard", { replace: true });
  };

  const handleProfileBasicsChange = (
    field: OnboardingProfileBasicsField,
    value: string
  ) => {
    setProfileBasics((current) => ({
      ...current,
      [field]: value,
    }));

    if (profileErrors[field] || profileErrors.general) {
      setProfileErrors((current) => ({
        ...current,
        [field]: "",
        general: "",
      }));
    }
  };

  const handleContinueToSiteSetup = () => {
    const validationResult = profileBasicsSchema.safeParse(profileBasics);

    if (!validationResult.success) {
      setProfileErrors(buildProfileBasicsErrors(validationResult.error));
      return;
    }

    const savedProfileBasics = parseProfileBasics(userProfileData);
    const hasChanges =
      !savedProfileBasics ||
      profileBasicsFields.some(
        ({ key }) => validationResult.data[key] !== savedProfileBasics[key]
      );

    if (!hasChanges) {
      setProfileErrors({});
      setStepIndex(1);
      return;
    }

    updateProfileMutation.mutate(validationResult.data);
  };
  const handleSiteSetupComplete = async (site?: Site) => {
    if (site) {
      setCreatedSite(site);
    }
    setStepIndex(2);
  };
  const handleInviteAdd = () => {
    const parsedEmail = inviteEmailSchema.safeParse(inviteEmail);
    if (!parsedEmail.success) {
      setInviteError(parsedEmail.error.issues[0]?.message ?? "Invalid email");
      return;
    }

    if (!createdSite) {
      setInviteError("Create a site before sending invites.");
      return;
    }

    const normalizedEmail = parsedEmail.data;
    const isDuplicate = queuedInvites.some(
      (invite) =>
        invite.email.toLowerCase() === normalizedEmail.toLowerCase() &&
        invite.role === inviteRole
    );

    if (isDuplicate) {
      setInviteError("That invite is already in the list.");
      return;
    }

    setQueuedInvites((current) => [
      ...current,
      { email: normalizedEmail, role: inviteRole },
    ]);
    setInviteEmail("");
    setInviteError(null);
  };
  const handleInviteDelete = (email: string, role: OnboardingInviteRole) => {
    setQueuedInvites((current) =>
      current.filter(
        (invite) => !(invite.email === email && invite.role === role)
      )
    );
  };
  const handleSendInvitesAndFinish = async () => {
    if (!createdSite) {
      setInviteError("Create a site before sending invites.");
      return;
    }

    try {
      for (const invite of queuedInvites) {
        await sendInviteMutation.mutateAsync({
          siteId: createdSite.id,
          email: invite.email,
          role: invite.role,
        });
      }

      await persistStatusAndExit("completed");
    } catch (error) {
      setInviteError(
        error instanceof Error ? error.message : "Failed to send invites"
      );
    }
  };

  const isSiteSetupStep = stepIndex === 1;
  const isInviteStep = stepIndex === 2;
  const stepLabel = `Step ${stepIndex + 1} of ${totalSteps}`;

  if (isSessionLoading || userProfilePending) {
    return <Loader className="min-h-screen" />;
  }

  if (userProfileIsError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <FormErrorAlert
          message={
            userProfileErrorValue instanceof Error
              ? userProfileErrorValue.message
              : "Failed to load your profile"
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-6 py-8 md:px-8 md:py-12">
        <main className="flex flex-1 py-8 md:py-12">
          {stepIndex === 0 ? (
            <section className="grid w-full gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(20rem,0.9fr)] lg:items-center lg:gap-12">
              <div className="flex flex-col items-center gap-6 text-center lg:items-start lg:text-left">
                <img
                  src="/icons/quick-actions/workers.png"
                  alt="Illustration highlighting profile setup"
                  className="mx-auto w-44 lg:mx-0 lg:w-52"
                  loading="lazy"
                />
                <div className="space-y-3">
                  <Heading variant="page" as="h2">
                    Add your profile
                  </Heading>
                  <p className="max-w-xl text-base leading-7 text-muted-foreground">
                    Start with the details that appear across site records,
                    approvals, and reporting.
                  </p>
                </div>
              </div>

              <Card className="border-border/60 shadow-sm">
                <CardHeader className="space-y-2">
                  <Text variant="eyebrow" as="p">
                    {stepLabel}
                  </Text>
                  <CardTitle>Your Profile</CardTitle>
                  <CardDescription>
                    A little bit about yourself.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {profileBasicsFields.map(({ key, label, placeholder }) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={key}>{label}</Label>
                      <Input
                        id={key}
                        type="text"
                        value={profileBasics[key]}
                        aria-invalid={Boolean(profileErrors[key])}
                        onChange={(event) =>
                          handleProfileBasicsChange(key, event.target.value)
                        }
                        placeholder={placeholder}
                      />
                      <FieldErrorMessage message={profileErrors[key]} />
                    </div>
                  ))}
                  <FormErrorAlert message={profileErrors.general} />
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={handleContinueToSiteSetup}
                      disabled={isBusy}
                      className="w-full"
                    >
                      {updateProfileMutation.isPending
                        ? "Saving details..."
                        : "Set up your site"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => void persistStatusAndExit("skipped")}
                      disabled={isBusy}
                      className="text-muted-foreground"
                    >
                      Skip
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>
          ) : isSiteSetupStep ? (
            <section className="mx-auto flex w-full max-w-4xl flex-col gap-6">
              <div className="flex flex-col items-center gap-6 text-center">
                <img
                  src="/icons/quick-actions/sites.png"
                  alt=""
                  className="mx-auto w-44"
                />
                <div className="space-y-2">
                  <Heading variant="page" as="h2">
                    Create your first site
                  </Heading>
                  <p className="max-w-2xl text-sm text-muted-foreground md:text-base">
                    Create your first site now or skip and do it later. You can
                    always add more from the Sites tab once you&apos;re inside
                    the app.
                  </p>
                </div>
              </div>
              <Text variant="eyebrow" as="p" className="text-center">
                {stepLabel}
              </Text>
              <div className="flex justify-center">
                <Button
                  variant="link"
                  onClick={() => setStepIndex(0)}
                  disabled={isBusy}
                  className="h-auto p-0 text-sm font-normal underline-offset-4 hover:underline"
                >
                  Back
                </Button>
              </div>
              <div className="mx-auto w-full max-w-3xl">
                <SiteSetup
                  formId={onboardingSiteSetupFormId}
                  onComplete={handleSiteSetupComplete}
                  className="border-border/60 shadow-sm"
                  showActions={false}
                  geofenceHelperText="This sets the sign-in boundary for the site. You can adjust it later if the geofence needs to be wider or tighter."
                />
              </div>
              <div className="mx-auto w-full max-w-3xl">
                <div className="grid gap-3 items-center">
                  <Button
                    type="submit"
                    form={onboardingSiteSetupFormId}
                    disabled={isBusy}
                    className="w-full"
                  >
                    Add Site
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => void persistStatusAndExit("skipped")}
                    disabled={isBusy}
                    className="w-full"
                  >
                    Skip
                  </Button>
                </div>
              </div>
            </section>
          ) : isInviteStep ? (
            <section className="mx-auto flex w-full max-w-4xl flex-col gap-6">
              <div className="flex flex-col items-center gap-6 text-center">
                <img
                  src="/icons/quick-actions/workers.png"
                  alt=""
                  className="mx-auto w-44"
                />
                <div className="space-y-2">
                  <Heading variant="page" as="h2">
                    Invite your team
                  </Heading>
                  <p className="max-w-2xl text-sm text-muted-foreground md:text-base">
                    Invite site managers and workers to{" "}
                    {createdSite?.name ?? "your site"}. Managers get a link to
                    the web dashboard. Workers get app download links for their
                    phone.
                  </p>
                </div>
              </div>
              <Text variant="eyebrow" as="p" className="text-center">
                {stepLabel}
              </Text>
              <div className="mx-auto w-full max-w-3xl rounded-md border border-border/60 bg-card p-4 shadow-sm">
                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_12rem_auto] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      value={inviteEmail}
                      aria-invalid={Boolean(inviteError)}
                      onChange={(event) => {
                        setInviteEmail(event.target.value);
                        if (inviteError) {
                          setInviteError(null);
                        }
                      }}
                      placeholder="person@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-role">Role</Label>
                    <Select
                      value={inviteRole}
                      onValueChange={(value) =>
                        setInviteRole(value as OnboardingInviteRole)
                      }
                    >
                      <SelectTrigger
                        id="invite-role"
                        className="w-full"
                        aria-invalid={Boolean(inviteError)}
                      >
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="site_manager">
                          Site manager
                        </SelectItem>
                        <SelectItem value="worker">Worker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    onClick={handleInviteAdd}
                    disabled={isBusy}
                    className="w-full md:w-auto"
                  >
                    Add
                  </Button>
                </div>
                <FormErrorAlert className="mt-3" message={inviteError} />
                <div className="mt-6 rounded-md border border-border/60">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="px-4">Email</TableHead>
                        <TableHead className="px-4">Role</TableHead>
                        <TableHead className="px-4 text-right">
                          Remove
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queuedInvites.length > 0 ? (
                        queuedInvites.map((invite) => (
                          <TableRow
                            key={`${invite.email}-${invite.role}`}
                            className="align-top"
                          >
                            <TableCell className="px-4 py-3 text-sm text-foreground">
                              {invite.email}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                              {invite.role === "site_manager"
                                ? "Site manager"
                                : "Worker"}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`Remove ${invite.email}`}
                                onClick={() =>
                                  handleInviteDelete(invite.email, invite.role)
                                }
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className="px-4 py-6 text-center text-sm text-muted-foreground"
                          >
                            No invites added yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
                <Button
                  type="button"
                  onClick={() => void handleSendInvitesAndFinish()}
                  disabled={isBusy}
                  className="w-full"
                >
                  {sendInviteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending invites...
                    </>
                  ) : queuedInvites.length > 0 ? (
                    "Send and finish"
                  ) : (
                    "Finish onboarding"
                  )}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => void persistStatusAndExit("completed")}
                  disabled={isBusy}
                  className="w-full"
                >
                  Finish without sending
                </Button>
              </div>
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
}

export default function Onboarding() {
  return <OnboardingContent />;
}
